import React, { useEffect, useRef } from "react"
import {
    Animated,
    Easing,
    Pressable,
    StyleSheet,
    Text,
    View,
} from "react-native"
import { theme } from "@common/theme/theme"
import { prefersReducedMotion } from "@common/liveMode/precall/precallLayout"
import { pressOnSpace } from "../utils/pressOnSpace"

const TRACK_WIDTH = 48
const TRACK_HEIGHT = 28
const THUMB = 22
const INSET = 3

/**
 * L'interrupteur d'engagement : un libellé, UNE ligne dessous — ce que
 * l'engagement fait gagner (`savingLabel`) — et l'interrupteur. Coupé, la
 * ligne garde ses mots et s'éteint : elle dit ce que le remettre ferait
 * gagner. Sans cadre : l'appelant le pose dans le sien.
 *
 * `checked` suit le geste du parent tout de suite ; `pending` dit qu'un
 * enregistrement est en cours, et l'interrupteur s'estompe le temps que le
 * serveur réponde.
 */
const CommitmentSwitch = ({
    label,
    savingLabel = null,
    checked,
    pending = false,
    onToggle,
}) => {
    const position = useRef(new Animated.Value(checked ? 1 : 0)).current
    useEffect(() => {
        Animated.timing(position, {
            toValue: checked ? 1 : 0,
            duration: prefersReducedMotion() ? 0 : 160,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: true,
        }).start()
    }, [checked, position])

    return (
        <View style={styles.row}>
            <View style={styles.texts}>
                <Text style={styles.label}>{label}</Text>
                {/* La ligne garde sa place quand elle se tait : la carte ne
                    saute pas pendant que le serveur répond. */}
                <Text style={[styles.saving, !checked && styles.savingOff]}>
                    {savingLabel || "\u00a0"}
                </Text>
            </View>
            <Pressable
                role="switch"
                aria-checked={checked}
                aria-busy={pending}
                aria-label={label}
                onPress={onToggle}
                {...pressOnSpace(onToggle)}
                style={[
                    styles.track,
                    checked && styles.trackOn,
                    pending && styles.trackPending,
                ]}
            >
                <Animated.View
                    style={[
                        styles.thumb,
                        {
                            transform: [
                                {
                                    translateX: position.interpolate({
                                        inputRange: [0, 1],
                                        outputRange: [
                                            0,
                                            TRACK_WIDTH - THUMB - INSET * 2,
                                        ],
                                    }),
                                },
                            ],
                        },
                    ]}
                />
            </Pressable>
        </View>
    )
}

const styles = StyleSheet.create({
    row: { flexDirection: "row", alignItems: "center", gap: 8 },
    texts: { flex: 1, minWidth: 0 },
    label: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 13,
        lineHeight: 17,
        color: theme.colors.primaryDark || theme.colors.primary,
    },
    saving: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12.5,
        lineHeight: 17,
        color: theme.colors.successDark,
        marginTop: 2,
    },
    savingOff: { color: theme.colors.grey },
    track: {
        width: TRACK_WIDTH,
        height: TRACK_HEIGHT,
        borderRadius: TRACK_HEIGHT / 2,
        padding: INSET,
        backgroundColor: theme.colors.light,
    },
    trackOn: { backgroundColor: theme.colors.primary },
    trackPending: { opacity: 0.55 },
    thumb: {
        width: THUMB,
        height: THUMB,
        borderRadius: THUMB / 2,
        backgroundColor: theme.colors.white,
        shadowColor: theme.colors.primaryDark,
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.3,
        shadowRadius: 3,
        elevation: 2,
    },
})

export default CommitmentSwitch
